import { BreadCrumbs, Filter, HomeIcon, Input } from '@isdd/idsk-ui-kit/index'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RELATION_TYPE } from '@isdd/metais-common/api'
import { CiLazySelect } from '@isdd/metais-common/components/ci-lazy-select/CiLazySelect'
import { ENTITY_OBJEKT_EVIDENCIE, JOIN_OPERATOR, OPERATOR_SEPARATOR, RELATION_FILTER_PREFIX } from '@isdd/metais-common/constants'
import { DynamicFilterAttributes, ExtendedAttribute } from '@isdd/metais-common/components/dynamicFilterAttributes/DynamicFilterAttributes'
import { MetainformationColumns } from '@isdd/metais-common/componentHelpers/ci/getCiDefaultMetaAttributes'
import { EnumType } from '@isdd/metais-common/api/generated/enums-repo-swagger'
import { AttributeProfile } from '@isdd/metais-common/api/generated/types-repo-swagger'
import { useSearchParams } from 'react-router-dom'
import { RowSelection } from '@isdd/metais-common/hooks/useRowSelection'
import { RouteNames } from '@isdd/metais-common/navigation/routeNames'

import { CIFilterData } from '@/pages/ci/[entityName]/entity'
import { getCiHowToBreadCrumb, useGetEntityParamsFromUrl } from '@/componentHelpers/ci'
import { MainContentWrapper } from '@/components/MainContentWrapper'
import { ListWrapper } from '@/components/list-wrapper/ListWrapper'
import { CiListWizard } from '@/components/wizards/CiListWizard'
import EvidenceObjectAttributeListContainer from '@/components/containers/evidence-object-attribute/EvidenceObjectAttributeListContainer'

const ObjektEvidencieRelType =
    `${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.Objekt_evidencie_ma_atribut_evidencie}` as 'ObjektEvidencieRelType'

const ISVSAOERelType = `${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.ISVS_je_poskytovatelom_AOE}` as 'ISVSAOERelType'
const POAOERelType = `${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.PO_je_poskytovatelom_AOE}` as 'POAOERelType'

export interface AtributObjektEvidencieFilterData extends CIFilterData {
    [ObjektEvidencieRelType]?: string
    [ISVSAOERelType]?: string
    [POAOERelType]?: string
}
export interface AtributObjektEvidenceFilterProps {
    defaultFilterValues: AtributObjektEvidencieFilterData
    entityName: string
    codePrefix: string
    constraintsData: (EnumType | undefined)[] | undefined
    attributes: ExtendedAttribute[] | undefined
    attributeProfiles: AttributeProfile[] | undefined
    rowSelection: RowSelection<object>
}

export const AtributObjektEvidencieFilter: React.FC<AtributObjektEvidenceFilterProps> = ({
    defaultFilterValues,
    attributes,
    attributeProfiles,
    constraintsData,
    codePrefix,
    entityName,
    rowSelection,
}) => {
    const { t } = useTranslation()
    const [urlParams] = useSearchParams()
    const objektEvidencieRelationFromUrl = urlParams.get(ObjektEvidencieRelType)
    const ISVSRelationFromUrl = urlParams.get(ISVSAOERelType)
    const POEvidencieRelationFromUrl = urlParams.get(POAOERelType)

    return (
        <Filter<AtributObjektEvidencieFilterData, object>
            rowSelection={rowSelection}
            defaultFilterValues={defaultFilterValues}
            form={({ register, filter, setValue, isOpen }) => {
                return (
                    <>
                        <CiLazySelect
                            ciType={'PO'}
                            label={t(`${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.PO_je_poskytovatelom_AOE}`)}
                            name={POAOERelType}
                            setValue={setValue}
                            defaultValue={
                                POEvidencieRelationFromUrl
                                    ? POEvidencieRelationFromUrl.includes(JOIN_OPERATOR)
                                        ? POEvidencieRelationFromUrl.split(JOIN_OPERATOR)
                                        : POEvidencieRelationFromUrl
                                    : defaultFilterValues[POAOERelType] ?? ''
                            }
                            isMulti
                            tabIndex={isOpen ? undefined : -1}
                        />
                        <CiLazySelect
                            ciType={'ISVS'}
                            label={t(`${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.ISVS_je_poskytovatelom_AOE}`)}
                            name={ISVSAOERelType}
                            setValue={setValue}
                            defaultValue={
                                ISVSRelationFromUrl
                                    ? ISVSRelationFromUrl.includes(JOIN_OPERATOR)
                                        ? ISVSRelationFromUrl.split(JOIN_OPERATOR)
                                        : ISVSRelationFromUrl
                                    : defaultFilterValues[ISVSAOERelType] ?? ''
                            }
                            isMulti
                            tabIndex={isOpen ? undefined : -1}
                        />
                        <CiLazySelect
                            ciType={ENTITY_OBJEKT_EVIDENCIE}
                            label={t('navMenu.lists.evidenceObjects')}
                            name={ObjektEvidencieRelType}
                            setValue={setValue}
                            defaultValue={
                                objektEvidencieRelationFromUrl
                                    ? objektEvidencieRelationFromUrl.includes(JOIN_OPERATOR)
                                        ? objektEvidencieRelationFromUrl.split(JOIN_OPERATOR)
                                        : objektEvidencieRelationFromUrl
                                    : defaultFilterValues[ObjektEvidencieRelType] ?? ''
                            }
                            isMulti
                            tabIndex={isOpen ? undefined : -1}
                        />
                        <Input
                            id="name"
                            label={t(`filter.${entityName}.name`)}
                            placeholder={t(`filter.namePlaceholder`)}
                            {...register('Gen_Profil_nazov')}
                        />

                        <Input
                            id="metais-code"
                            label={t('filter.metaisCode.label')}
                            placeholder={codePrefix}
                            {...register('Gen_Profil_kod_metais')}
                        />
                        <DynamicFilterAttributes
                            setValue={setValue}
                            defaults={defaultFilterValues}
                            filterData={{
                                attributeFilters: filter.attributeFilters ?? {},
                                metaAttributeFilters: filter.metaAttributeFilters ?? {},
                            }}
                            attributes={attributes as ExtendedAttribute[]}
                            attributeProfiles={attributeProfiles}
                            constraintsData={constraintsData}
                            isFocusable={isOpen}
                            ignoreInputNames={[MetainformationColumns.OWNER]}
                        />
                    </>
                )
            }}
        />
    )
}

export const AtributObjektEvidencieListPage: React.FC = () => {
    const { entityName: ciType } = useGetEntityParamsFromUrl()
    const { t } = useTranslation()

    const defaultFilterValues: AtributObjektEvidencieFilterData = {
        Gen_Profil_nazov: '',
        Gen_Profil_kod_metais: '',
        [ObjektEvidencieRelType]: '',
        [ISVSAOERelType]: '',
        [POAOERelType]: '',
    }

    return (
        <>
            <EvidenceObjectAttributeListContainer<AtributObjektEvidencieFilterData>
                entityName={ciType ?? ''}
                defaultFilterValues={defaultFilterValues}
                ListComponent={(props) => (
                    <>
                        <BreadCrumbs
                            withWidthContainer
                            links={[
                                { label: t('breadcrumbs.home'), href: '/', icon: HomeIcon },
                                ...getCiHowToBreadCrumb(props.entityName, t),
                                { label: t('breadcrumbs.wiki.REGISTER_DATA_RIGHTS_OBLIGATIONS'), href: RouteNames.REGISTER_DATA_RIGHTS_OBLIGATIONS },
                                {
                                    label: t('titles.ciList', { ci: props.ciTypeData?.name }) ?? '',
                                    href: `/ci/${ciType}`,
                                },
                            ]}
                        />
                        {!props.isLoading && <CiListWizard />}
                        <MainContentWrapper>
                            <ListWrapper
                                customFilter={(filterProps) => <AtributObjektEvidencieFilter {...filterProps} />}
                                {...props}
                                attributes={props.attributes}
                            />
                        </MainContentWrapper>
                    </>
                )}
            />
        </>
    )
}
